import { z } from "zod";

/**
 * DBIndex Connector protocol v1. HTTPS/JSON, outbound-only from the customer network.
 *
 * Auth: the connector generates an Ed25519 keypair locally at enrollment. DBIndex stores
 * only the PUBLIC key. Sessions are minted by signing a server-issued challenge; access
 * tokens live 15 minutes. A DBIndex database breach therefore yields no usable connector
 * credential, and no source DB credential (those never leave the customer network).
 */
export const CONNECTOR_PROTOCOL_VERSION = 1;

export const EnrollRequest = z.object({
  enrollmentToken: z.string().min(20).max(200), // one-time, 30-minute, hashed at rest
  publicKeyJwk: z.object({ kty: z.literal("OKP"), crv: z.literal("Ed25519"), x: z.string().min(40).max(64) }),
  connectorVersion: z.string().max(32),
  adapters: z.array(z.string().max(32)).max(20),
  hostname: z.string().max(128).optional(),
});
export const EnrollResponse = z.object({ connectorId: z.string(), dataSourceId: z.string(), serverSigningKeyJwk: z.record(z.string(), z.unknown()) });

export const SessionRequest = z.object({ connectorId: z.string(), challenge: z.string(), timestamp: z.number().int(), signature: z.string() });
export const SessionResponse = z.object({ accessToken: z.string(), expiresAt: z.number().int() });

/** Jobs are a CLOSED set. There is no "run command" or "run SQL" job. */
export const ConnectorJob = z.discriminatedUnion("type", [
  z.object({ type: z.literal("test_connection"), jobId: z.string() }),
  z.object({ type: z.literal("inspect_schema"), jobId: z.string(), schemas: z.array(z.string()).max(20).optional() }),
  /** Sample values for classification. Connector pre-masks values (shape-preserving) before returning. */
  z.object({ type: z.literal("sample_columns"), jobId: z.string(), table: z.string(), schema: z.string(), limit: z.number().int().min(1).max(50) }),
  /** Contract delivered as a signed envelope; connector verifies signature + local policy before syncing. */
  z.object({ type: z.literal("start_sync"), jobId: z.string(), datasetId: z.string(), contractEnvelope: z.unknown(), mode: z.enum(["snapshot_then_incremental", "incremental"]) }),
  z.object({ type: z.literal("stop_sync"), jobId: z.string(), datasetId: z.string() }),
]);
export type ConnectorJob = z.infer<typeof ConnectorJob>;

export const JobResult = z.object({
  jobId: z.string(),
  ok: z.boolean(),
  error: z.string().max(2000).optional(),
  result: z.unknown().optional(),
});

export const SourceColumnInfo = z.object({
  name: z.string(), dataType: z.string(), nullable: z.boolean(), isPrimaryKey: z.boolean().default(false),
  default: z.string().nullable().optional(), comment: z.string().nullable().optional(),
});
export const SourceTableInfo = z.object({
  schema: z.string(), name: z.string(), kind: z.enum(["table", "view"]).default("table"), rowEstimate: z.number().int().nonnegative(),
  columns: z.array(SourceColumnInfo), primaryKey: z.array(z.string()),
  foreignKeys: z.array(z.object({ columns: z.array(z.string()), refSchema: z.string(), refTable: z.string(), refColumns: z.array(z.string()) })).default([]),
  indexes: z.array(z.object({ name: z.string(), columns: z.array(z.string()), unique: z.boolean() })).default([]),
  changeTracking: z.object({ cursorCandidates: z.array(z.string()), cdc: z.boolean() }).optional(),
});
export const SchemaSnapshot = z.object({
  engine: z.string(), serverVersion: z.string(), database: z.string(), capturedAt: z.string(), tables: z.array(SourceTableInfo),
});
export type SchemaSnapshot = z.infer<typeof SchemaSnapshot>;
export type SourceTableInfo = z.infer<typeof SourceTableInfo>;

export const ChangeOp = z.enum(["UPSERT", "DELETE", "SNAPSHOT_BEGIN", "SNAPSHOT_END", "HEARTBEAT"]);
export const ChangeRecord = z.object({
  op: ChangeOp,
  entity: z.string(),
  /** HMAC(connector-local secret, canonical PK). Stable, non-reversible by DBIndex. */
  key: z.string().max(128).optional(),
  /** Monotonic version (cursor value, LSN, or ts) for last-writer-wins idempotency. */
  version: z.string().max(64).optional(),
  sourceTs: z.string().optional(),
  /** Already-sanitised payload containing ONLY serving fields. */
  payload: z.record(z.string(), z.unknown()).optional(),
});
export type ChangeRecord = z.infer<typeof ChangeRecord>;

export const IngestBatch = z.object({
  datasetId: z.string(),
  contractVersion: z.number().int(),
  schemaVersion: z.number().int(),
  // idempotency key: connectorId + datasetId + seq (30 + 1 + 30 + 1 + up to 19 digits = 81); 64 broke at seq ≥ 100
  batchId: z.string().min(1).max(128),
  seq: z.number().int().nonnegative(),
  sentAt: z.string(),
  records: z.array(ChangeRecord).max(5000),
  checkpoint: z.record(z.string(), z.string()).default({}),
  timings: z.object({ sourceObservedAt: z.string().optional(), connectorSentAt: z.string() }).optional(),
});
export type IngestBatch = z.infer<typeof IngestBatch>;

export const Heartbeat = z.object({
  connectorVersion: z.string(),
  uptimeSeconds: z.number().int(),
  // Connectors ≤0.1 report state "" before their first sync cycle; treat it as idle.
  datasets: z.array(z.object({ datasetId: z.string(), state: z.preprocess((v) => (v === "" || v == null ? "idle" : v), z.enum(["idle", "snapshotting", "streaming", "error", "paused"])), lagSeconds: z.number().nullable(), recordsPerSec: z.number(), lastCheckpoint: z.record(z.string(), z.string()).default({}), lastError: z.string().max(500).nullable().default(null) })).default([]),
});
